import React from 'react';
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Chip,
  IconButton,
  Grid,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Tooltip,
  Collapse,
  ButtonBase,
  alpha,
  useTheme
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, Code as CodeIcon, ViewList as ViewListIcon, AutoFixHigh as AutoFixHighIcon, ExpandMore as ExpandMoreIcon, FiberManualRecord as RecordIcon } from '@mui/icons-material';
import { AttackType, AttackConfigField, ParsedConfigSchema } from '../types/attacks';
import { CbPartType, CbPartConfig, CbPart, CbParam, CB_PART_LABELS, defaultCbPart, defaultPayloadFunction, PayloadFunction } from '../types/payloadTypes';
import { ShortcutConfig, shortcutToLabel, usePayloadShortcut } from '../hooks/usePayloadShortcut';
import { scanPayloadPlaceholders, insertPlaceholderAtCursor } from '../utils/payloadScanner';
import PayloadFunctionsCard from './PayloadFunctionsCard';
import { parseTarget } from '../utils/targetUtils';
import api from '../services/api';

interface AttackConfigFormProps {
  attackType: AttackType;
  configuration: Record<string, any>;
  onChange: (configuration: Record<string, any>) => void;
  isEditMode?: boolean;
  restrictedFields?: string[];
  showConfigPreview?: boolean;
}

const EXACT_PAYLOAD_PLACEHOLDER_TOKEN_REGEX = /^§(\d+)§$/;

// Carpet bombing fields may lag behind in DB-stored config_schema. Provide UI fallbacks so
// operators still get the right configuration controls immediately.
const CARPET_BOMBING_FALLBACK_FIELDS: Record<string, AttackConfigField> = {
  ssl_ports: {
    name: 'ssl_ports',
    type: 'array',
    description: 'Target ports for SSL/TLS Flood.',
    required: false,
    items: { type: 'integer' },
    default: [443],
  },
  ssl_timeout: {
    name: 'ssl_timeout',
    type: 'integer',
    description: 'Socket timeout (seconds) for SSL/TLS Flood.',
    required: false,
    minimum: 1,
    maximum: 120,
    default: 20,
  },
  tcp_conn_ports: {
    name: 'tcp_conn_ports',
    type: 'array',
    description: 'Target ports for TCP Connection Flood.',
    required: false,
    items: { type: 'integer' },
    default: [80, 443],
  },
  tcp_conn_connection_time: {
    name: 'tcp_conn_connection_time',
    type: 'number',
    description: 'How long to hold each TCP connection open (seconds).',
    required: false,
    minimum: 0.1,
    maximum: 600,
    default: 30,
  },
  get_flood_timeout: {
    name: 'get_flood_timeout',
    type: 'integer',
    description: 'HTTP timeout (seconds) for GET Flood.',
    required: false,
    minimum: 1,
    maximum: 120,
    default: 20,
  },
  post_data_size: {
    name: 'post_data_size',
    type: 'integer',
    description: 'POST payload size (bytes) for Large Data POST Flood.',
    required: false,
    minimum: 0,
    maximum: 5000000,
    default: 10000,
  },
  post_content_type: {
    name: 'post_content_type',
    type: 'string',
    description: 'Optional Content-Type override for Large Data POST Flood.',
    required: false,
    default: '',
  },
  slowpost_content_length: {
    name: 'slowpost_content_length',
    type: 'integer',
    description: 'Content-Length to advertise and then slowly send (bytes).',
    required: false,
    minimum: 0,
    maximum: 50000000,
    default: 10000,
  },
  slowpost_interval: {
    name: 'slowpost_interval',
    type: 'number',
    description: 'Delay between slow-send chunks (seconds).',
    required: false,
    minimum: 0.01,
    maximum: 30,
    default: 0.1,
  },
  slowpost_send_rate: {
    name: 'slowpost_send_rate',
    type: 'integer',
    description: 'Bytes per chunk per interval for Slow POST.',
    required: false,
    minimum: 1,
    maximum: 65535,
    default: 10,
  },
  slowloris_ports: {
    name: 'slowloris_ports',
    type: 'array',
    description: 'Target ports for Slow GET (Slowloris).',
    required: false,
    items: { type: 'integer' },
    default: [80, 443],
  },
  slowloris_sleep_time: {
    name: 'slowloris_sleep_time',
    type: 'number',
    description: 'Interval between keep-alive header sends (seconds).',
    required: false,
    minimum: 0.5,
    maximum: 120,
    default: 15,
  },
  slowloris_packet_size: {
    name: 'slowloris_packet_size',
    type: 'integer',
    description: 'Size of each keep-alive header value (bytes).',
    required: false,
    minimum: 1,
    maximum: 4096,
    default: 16,
  },
};

// Returns the set of field names that should be hidden given the current config state.
// Only adds attack-specific logic where needed; generic attacks return an empty set.
const computeHiddenFields = (attackTypeId: string, config: Record<string, any>): Set<string> => {
  const hidden = new Set<string>();

  // expand_per_target is managed inline per-target row in CreateScenarioDialog
  hidden.add('expand_per_target');

  if (attackTypeId === 'attack_id_76') {

    const mode: string = config.mode || 'random';
    const randomInterval = Boolean(config.random_interval);

    if (mode === 'coordinated') {
      // Coordinated: fixed UTC-aligned slot — no random duration concept
      hidden.add('random_interval');
      hidden.add('interval_seconds_min');
      hidden.add('interval_seconds_max');
    } else {
      // Random mode
      if (!randomInterval) {
        // Fixed slot: hide the min/max range
        hidden.add('interval_seconds_min');
        hidden.add('interval_seconds_max');
      } else {
        // Per-bot random duration: hide the single fixed value
        hidden.add('interval_seconds');
      }
    }

    // Sub-attack configs: only show when the corresponding attack type is enabled.
    // Defaults must match backend transform defaults for carpet bombing.
    const udpEnabled = config.include_udp_flood !== false; // default true
    const tcpEnabled = config.include_tcp_flood !== false; // default true
    const icmpEnabled = config.include_icmp_flood !== false; // default true
    const fragEnabled = config.include_fragmented_udp_flood === true; // default false

    const sslEnabled = config.include_ssl_flood === true; // default false
    const tcpConnEnabled = config.include_tcp_connection_flood === true; // default false
    const getEnabled = config.include_get_flood === true; // default false
    const postEnabled = config.include_post_flood === true; // default false
    const slowPostEnabled = config.include_slow_post === true; // default false
    const slowGetEnabled = config.include_slow_get === true; // default false

    // attack_interval is shared across volumetric sub-attacks; hide it if none are enabled.
    if (!(udpEnabled || tcpEnabled || icmpEnabled || fragEnabled)) {
      hidden.add('attack_interval');
    }

    if (!udpEnabled) {
      hidden.add('udp_ports');
      hidden.add('udp_data_size');
    }
    if (!tcpEnabled) {
      hidden.add('tcp_ports');
      hidden.add('tcp_flags');
    }
    if (!icmpEnabled) {
      hidden.add('icmp_type');
      hidden.add('icmp_packet_size');
    }
    if (!fragEnabled) {
      hidden.add('fragmented_udp_ports');
      hidden.add('fragmented_udp_data_size');
      hidden.add('fragment_size');
    }

    if (!sslEnabled) {
      hidden.add('ssl_ports');
      hidden.add('ssl_timeout');
    }

    if (!tcpConnEnabled) {
      hidden.add('tcp_conn_ports');
      hidden.add('tcp_conn_connection_time');
    }

    if (!getEnabled) {
      hidden.add('get_flood_timeout');
    }

    if (!postEnabled) {
      hidden.add('post_data_size');
      hidden.add('post_content_type');
    }

    if (!slowPostEnabled) {
      hidden.add('slowpost_content_length');
      hidden.add('slowpost_interval');
      hidden.add('slowpost_send_rate');
    }

    if (!slowGetEnabled) {
      hidden.add('slowloris_ports');
      hidden.add('slowloris_sleep_time');
      hidden.add('slowloris_packet_size');
    }
  }

  if (attackTypeId === 'attack_id_70') {
    // Toggle defaults: udp/tcp/icmp on by default, fragmented off.
    const udpEnabled = config.udp_flood !== false;
    const tcpEnabled = config.tcp_flood !== false;
    const icmpEnabled = config.icmp_flood !== false;
    const fragEnabled = config.fragmented_udp_flood === true;

    // attack_interval is shared; hide only when every sub-attack is disabled.
    if (!(udpEnabled || tcpEnabled || icmpEnabled || fragEnabled)) {
      hidden.add('attack_interval');
    }

    if (!udpEnabled) {
      hidden.add('udp_ports');
      hidden.add('udp_data_size');
    }
    if (!tcpEnabled) {
      hidden.add('tcp_ports');
    }
    // icmp has no unique config fields beyond the shared interval
    if (!fragEnabled) {
      hidden.add('fragmented_udp_ports');
      hidden.add('fragmented_udp_data_size');
    }
  }

  return hidden;
};

const AttackConfigForm: React.FC<AttackConfigFormProps> = ({
  attackType,
  configuration,
  onChange,
  isEditMode = false,
  restrictedFields = ['targets'],
  showConfigPreview = true
}) => {
  const theme = useTheme();
  // State for managing object field inputs
  const [objectFieldStates, setObjectFieldStates] = React.useState<Record<string, { newKey: string; newValue: string }>>({});
  
  // State for view mode and JSON editing
  const [viewMode, setViewMode] = React.useState<'form' | 'json'>('form');
  const [jsonString, setJsonString] = React.useState('');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  
  // State for collapsing long arrays
  const [expandedArrays, setExpandedArrays] = React.useState<Record<string, boolean>>({});
  
  // State for advanced parameters accordion
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [numericFieldErrors, setNumericFieldErrors] = React.useState<Record<string, string>>({});
  const [recordingShortcut, setRecordingShortcut] = React.useState(false);

  // Update JSON string when configuration changes
  React.useEffect(() => {
    setJsonString(JSON.stringify(configuration, null, 2));
  }, [configuration]);

  // Keep UI defaults aligned with backend defaults for carpet bombing (attack_id_76).
  // Without this, switches render unchecked for undefined values even when the backend
  // assumes a default of true/false.
  React.useEffect(() => {
    if (attackType.id !== 'attack_id_76') return;

    const defaults: Record<string, any> = {
      mode: 'random',
      interval_seconds: 300,
      random_interval: false,
      interval_seconds_min: 10,
      interval_seconds_max: 60,
      attack_interval: 'u50',

      include_udp_flood: true,
      include_tcp_flood: true,
      include_icmp_flood: true,
      include_fragmented_udp_flood: false,

      include_ssl_flood: false,
      include_tcp_connection_flood: false,
      include_get_flood: false,
      include_post_flood: false,
      include_slow_post: false,
      include_slow_get: false,

      udp_ports: [80, 443],
      udp_data_size: 1200,
      tcp_ports: [80, 443],
      icmp_packet_size: 64,
      icmp_type: 8,
    };

    let changed = false;
    const nextConfig: Record<string, any> = { ...configuration };
    Object.entries(defaults).forEach(([key, value]) => {
      if (nextConfig[key] === undefined) {
        nextConfig[key] = value;
        changed = true;
      }
    });

    if (changed) onChange(nextConfig);
  }, [attackType.id, configuration, onChange]);

  // Defaults for Combined Volumetric (attack_id_70)
  React.useEffect(() => {
    if (attackType.id !== 'attack_id_70') return;

    const defaults: Record<string, any> = {
      udp_flood: true,
      tcp_flood: true,
      icmp_flood: true,
      fragmented_udp_flood: false,
      attack_interval: 'u50',
      udp_ports: [80, 443],
      udp_data_size: 1200,
      tcp_ports: [80, 443],
      fragmented_udp_ports: [80, 443],
      fragmented_udp_data_size: 4000,
    };

    let changed = false;
    const nextConfig: Record<string, any> = { ...configuration };
    Object.entries(defaults).forEach(([key, value]) => {
      if (nextConfig[key] === undefined) {
        nextConfig[key] = value;
        changed = true;
      }
    });

    if (changed) onChange(nextConfig);
  }, [attackType.id, configuration, onChange]);

  const parseSchema = (schema: Record<string, any>): ParsedConfigSchema => {
    const properties = schema.properties || {};
    const required = schema.required || [];
    
    const fields: AttackConfigField[] = Object.entries(properties).map(([name, prop]: [string, any]) => ({
      name,
      type: prop.type,
      format: prop.format,
      description: prop.description,
      required: required.includes(name),
      minimum: prop.minimum,
      maximum: prop.maximum,
      default: prop.default,
      enum: prop.enum,
      items: prop.items,
      additionalProperties: prop.additionalProperties
    }));

    return { fields, required };
  };

  const parsedSchema = parseSchema(attackType.config_schema);

  const getFieldGridProps = (field: AttackConfigField): { xs: 12; sm?: 6 | 12; md?: 4 | 6 | 12 } => {
    // Toggles look better 2-per-row
    if (field.type === 'boolean') {
      return { xs: 12, sm: 6, md: 6 };
    }

    // Everything else (arrays, objects, strings, numbers) full width for clean vertical layout
    return { xs: 12, sm: 12, md: 12 };
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    console.log(`handleFieldChange called for ${fieldName}:`, value);
    const newConfig = { ...configuration, [fieldName]: value };
    console.log('New configuration:', newConfig);
    onChange(newConfig);
  };

  const handleArrayAdd = (fieldName: string) => {
    const currentArray = configuration[fieldName] || [];
    const newArray = [...currentArray, ''];
    handleFieldChange(fieldName, newArray);
  };

  const handleArrayRemove = (fieldName: string, index: number) => {
    const currentArray = configuration[fieldName] || [];
    const newArray = currentArray.filter((_: any, i: number) => i !== index);
    handleFieldChange(fieldName, newArray);
  };

  // Coerce a raw text value into the type the backend schema expects for an
  // array item. Numeric-array items (e.g. `port`) must be sent as numbers, not
  // strings — otherwise jsonschema validation fails ("'50001' does not match
  // ...§N§..."). Empty input, in-progress literals, and §N§ payload
  // placeholders are left as strings so typing and placeholders keep working.
  const coerceArrayItemValue = (field: AttackConfigField | undefined, value: any): any => {
    if (!field || !isNumericArrayField(field) || typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    if (trimmed === '' || EXACT_PAYLOAD_PLACEHOLDER_TOKEN_REGEX.test(trimmed)) {
      return value;
    }
    if (field.items?.type === 'integer') {
      return /^-?\d+$/.test(trimmed) ? parseInt(trimmed, 10) : value;
    }
    return /^-?(?:\d+|\d+\.\d+|\d*\.\d+)$/.test(trimmed) ? parseFloat(trimmed) : value;
  };

  const handleArrayItemChange = (fieldName: string, index: number, value: any, field?: AttackConfigField) => {
    const currentArray = configuration[fieldName] || [];
    const newArray = [...currentArray];
    newArray[index] = coerceArrayItemValue(field, value);
    handleFieldChange(fieldName, newArray);
  };

  function isNumericArrayField(field: AttackConfigField): boolean {
    return field.type === 'array' && (field.items?.type === 'integer' || field.items?.type === 'number');
  }

  const getArrayItemInputProps = (field: AttackConfigField, index: number) => {
    const isNumericItemsField = isNumericArrayField(field);
    return {
      'data-field-name': field.name,
      'data-field-index': String(index),
      ...(isNumericItemsField ? { 'data-field-value-type': 'number' } : {}),
      ...(field.items?.type === 'integer'
        ? { inputMode: 'numeric', pattern: '-?[0-9]*' }
        : {}),
      ...(field.items?.type === 'number'
        ? { inputMode: 'decimal', pattern: '-?[0-9]*[.,]?[0-9]*' }
        : {}),
    } as any;
  };

  const handleObjectAdd = (fieldName: string, key: string, value: string) => {
    console.log(`Adding to ${fieldName}: ${key} = ${value}`);
    const currentObject = configuration[fieldName] || {};
    const newObject = { ...currentObject, [key]: value };
    console.log('New object value:', newObject);
    handleFieldChange(fieldName, newObject);
  };

  const handleObjectRemove = (fieldName: string, key: string) => {
    const currentObject = configuration[fieldName] || {};
    const newObject = { ...currentObject };
    delete newObject[key];
    handleFieldChange(fieldName, newObject);
  };

  // Handle JSON editor changes
  const handleJsonChange = (value: string) => {
    setJsonString(value);
    
    try {
      const parsed = JSON.parse(value);
      
      // Prevent changing targets in JSON view
      if (parsed.targets && JSON.stringify(parsed.targets) !== JSON.stringify(configuration.targets)) {
        setJsonError('Cannot modify targets in JSON view. Targets are set when creating the scenario.');
        return;
      }
      
      // Basic schema validation - check required fields
      const requiredFields = parsedSchema.required.filter((f: string) => f !== 'targets' && f !== 'threads');
      const missingFields = requiredFields.filter(field => !(field in parsed));
      
      if (missingFields.length > 0) {
        setJsonError(`Missing required fields: ${missingFields.join(', ')}`);
        return;
      }
      
      setJsonError(null);
      onChange(parsed);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'form' | 'json') => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // ─── Payload Functions ───────────────────────────────────────────────────
  const containerRef = React.useRef<HTMLDivElement>(null);
  const configRef = React.useRef<Record<string, any>>(configuration);
  React.useEffect(() => { configRef.current = configuration; }, [configuration]);

  const onPayloadInsert = React.useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement, n: number) => {
      const newValue = insertPlaceholderAtCursor(element, n);
      const el = element as HTMLElement;
      const fieldName = el.getAttribute('data-field-name');
      const fieldIndex = el.getAttribute('data-field-index');
      const fieldKey = el.getAttribute('data-field-key');
      const fieldValueType = el.getAttribute('data-field-value-type');
      if (!fieldName) return;

      // If placeholder is inserted into a numeric field, default its function to random_number.
      // Do not override existing function config for the same placeholder id.
      if (fieldValueType === 'number') {
        const currentFnsRaw = (configRef.current as any).payload_functions;
        const currentFns: PayloadFunction[] = Array.isArray(currentFnsRaw) ? currentFnsRaw : [];
        if (!currentFns.some((fn) => fn.id === n)) {
          const nextFns = [...currentFns, { ...defaultPayloadFunction(n), ...defaultCbPart('random_number'), id: n }];
          nextFns.sort((a, b) => a.id - b.id);
          handleFieldChange('payload_functions', nextFns);
        }
      }

      if (fieldIndex !== null && fieldIndex !== '') {
        handleArrayItemChange(fieldName, parseInt(fieldIndex, 10), newValue);
      } else if (fieldKey !== null && fieldKey !== '') {
        handleObjectAdd(fieldName, fieldKey, newValue);
      } else {
        handleFieldChange(fieldName, newValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configuration],
  );

  const { shortcut, updateShortcut } = usePayloadShortcut(configRef, onPayloadInsert, containerRef);

  const handleShortcutRecordKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const modifiers = ['Control', 'Alt', 'Shift', 'Meta'];
    if (modifiers.includes(e.key)) return;
    const newShortcut: ShortcutConfig = {
      key: e.key,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
    };
    updateShortcut(newShortcut);
    setRecordingShortcut(false);
  }, [updateShortcut]);

  const getPayloadFunctions = (): PayloadFunction[] => {
    const raw = (configuration as any).payload_functions;
    return Array.isArray(raw) ? raw : [];
  };

  const updatePayloadFunctions = (fns: PayloadFunction[]) => {
    handleFieldChange('payload_functions', fns);
  };

  const ensureNumericPayloadFunction = React.useCallback((placeholderId: number) => {
    const currentFnsRaw = (configRef.current as any).payload_functions;
    const currentFns: PayloadFunction[] = Array.isArray(currentFnsRaw) ? currentFnsRaw : [];
    if (currentFns.some((fn) => fn.id === placeholderId)) return;
    const nextFns = [...currentFns, { ...defaultPayloadFunction(placeholderId), ...defaultCbPart('random_number'), id: placeholderId }];
    nextFns.sort((a, b) => a.id - b.id);
    handleFieldChange('payload_functions', nextFns);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderField = (field: AttackConfigField) => {
    const value = configuration[field.name] ?? field.default ?? '';
    const isRestricted = isEditMode && restrictedFields.includes(field.name);

    switch (field.type) {
      case 'string':
        // Handle enum fields with dropdown
        if (field.enum && field.enum.length > 0) {
          return (
            <FormControl key={field.name} fullWidth sx={{ mb: 2 }}>
              <InputLabel required={field.required}>{field.name}</InputLabel>
              <Select
                value={value || field.default || ''}
                onChange={(e) => !isRestricted && handleFieldChange(field.name, e.target.value)}
                label={field.name}
                disabled={isRestricted}
              >
                {field.enum.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {(field.description || isRestricted) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  {isRestricted ? `${field.description} (Cannot be modified in edit mode)` : field.description}
                </Typography>
              )}
            </FormControl>
          );
        }
        
        if (field.format === 'uri' || field.format === 'hostname' || field.format === 'ipv4') {
          return (
            <TextField
              key={field.name}
              fullWidth
              label={field.name}
              value={value}
              onChange={(e) => !isRestricted && handleFieldChange(field.name, e.target.value)}
              helperText={isRestricted ? `${field.description} (Cannot be modified in edit mode)` : field.description}
              required={field.required}
              disabled={isRestricted}
              type={field.format === 'ipv4' ? 'text' : 'url'}
              placeholder={
                field.format === 'uri' ? 'https://example.com/path' :
                field.format === 'hostname' ? 'example.com' :
                field.format === 'ipv4' ? '192.168.1.1' : ''
              }
              inputProps={{ 'data-field-name': field.name } as any}
              sx={{}}
            />
          );
        }
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name.replace(/_/g, ' ').toUpperCase()}
            value={value}
            onChange={(e) => !isRestricted && handleFieldChange(field.name, e.target.value)}
            helperText={isRestricted ? `${field.description} (Cannot be modified)` : field.description}
            required={field.required}
            disabled={isRestricted}
            multiline={field.name.includes('payload') || field.name.includes('data')}
            rows={field.name.includes('payload') || field.name.includes('data') ? 3 : 1}
            inputProps={{ 'data-field-name': field.name } as any}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '1.4rem',
                '& input': { py: 1.2 },
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '1.1rem',
                fontWeight: 600,
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.95rem',
              }
            }}
          />
        );

      case 'integer':
      case 'number':
        const numericFieldError = numericFieldErrors[field.name] || '';
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name.replace(/_/g, ' ').toUpperCase()}
            type="text"
            value={value}
            onChange={(e) => {
              if (!isRestricted) {
                const inputValue = e.target.value.trim();
                const placeholderMatch = inputValue.match(EXACT_PAYLOAD_PLACEHOLDER_TOKEN_REGEX);
                if (placeholderMatch) {
                  const placeholderId = parseInt(placeholderMatch[1], 10);
                  if (!isNaN(placeholderId)) {
                    ensureNumericPayloadFunction(placeholderId);
                  }
                  setNumericFieldErrors((prev) => ({ ...prev, [field.name]: '' }));
                  // Keep placeholders as string values (e.g. "§1§") for runtime resolution.
                  handleFieldChange(field.name, inputValue);
                  return;
                }

                let numValue;
                if (inputValue === '' || inputValue === null || inputValue === undefined) {
                  setNumericFieldErrors((prev) => ({ ...prev, [field.name]: '' }));
                  numValue = field.default !== undefined ? field.default : 0;
                } else {
                  const isValidInteger = /^-?\d+$/.test(inputValue);
                  const isValidNumber = /^-?(?:\d+|\d+\.\d+|\d*\.\d+)$/.test(inputValue);
                  const isValidNumericLiteral = field.type === 'integer' ? isValidInteger : isValidNumber;

                  if (!isValidNumericLiteral) {
                    setNumericFieldErrors((prev) => ({
                      ...prev,
                      [field.name]: field.type === 'integer'
                        ? 'Enter an integer or press Ctrl+I for a placeholder like §1§'
                        : 'Enter a number or press Ctrl+I for a placeholder like §1§',
                    }));
                    return;
                  }

                  setNumericFieldErrors((prev) => ({ ...prev, [field.name]: '' }));
                  numValue = field.type === 'integer'
                    ? parseInt(inputValue, 10)
                    : parseFloat(inputValue);
                }

                handleFieldChange(field.name, numValue);
              }
            }}
            error={Boolean(numericFieldError)}
            helperText={
              numericFieldError ||
              (isRestricted ? `${field.description} (Cannot be modified)` : field.description)
            }
            required={field.required}
            disabled={isRestricted}
            inputProps={{
              'data-field-name': field.name,
              'data-field-value-type': 'number',
              inputMode: field.type === 'integer' ? 'numeric' : 'decimal',
              pattern: field.type === 'integer' ? '-?[0-9]*' : '-?[0-9]*[.,]?[0-9]*',
              min: field.minimum,
              max: field.maximum,
              step: field.type === 'integer' ? 1 : 0.1
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '1.4rem',
                '& input': { py: 1.2 },
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '1.1rem',
                fontWeight: 600,
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.95rem',
              }
            }}
          />
        );

      case 'boolean':
        return (
          <Box
            key={field.name}
            sx={{
              p: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.25)}`,
              borderRadius: 2,
              background: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.03 : 0.08),
              height: '100%',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(value)}
                  onChange={(e) => !isRestricted && handleFieldChange(field.name, e.target.checked)}
                  disabled={isRestricted}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography 
                    variant="body1"
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: '1.15rem',
                    }}
                  >
                    {field.name.replace(/_/g, ' ').toUpperCase()}
                  </Typography>
                  {field.description && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '1rem' }}>
                      {isRestricted ? `${field.description} (Cannot be modified)` : field.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
        );

      case 'array':
        const arrayValue = Array.isArray(value) ? value : [];
        const supportedFieldNames = ['user_agents', 'dns-port', 'common-tcp-ports'];
        const supportsCommonValues = supportedFieldNames.includes(field.name);
        
        // Collapse long arrays - show first 3 items by default
        const isExpanded = expandedArrays[field.name] || arrayValue.length <= 3;
        const displayItems = isExpanded ? arrayValue : arrayValue.slice(0, 3);
        const hiddenCount = arrayValue.length - 3;
        const numericArrayField = isNumericArrayField(field);
        
        const isCompactArray =
          field.name !== 'targets' &&
          arrayValue.every((v: any) => typeof v === 'string' || typeof v === 'number') &&
          arrayValue.every((v: any) => String(v ?? '').length <= 20);

        return (
          <Box key={field.name} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '1.15rem',
                  fontFamily: '"Space Mono", monospace',
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {field.name.replace(/_/g, ' ').toUpperCase()}
              </Typography>
              {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
              {isRestricted && <Chip label="Read-only" size="small" color="default" sx={{ ml: 1 }} />}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                {supportsCommonValues && !isRestricted && (
                  <Tooltip title="Fill with common values">
                    <IconButton
                      size="small"
                      onClick={async () => {
                        try {
                          const commonField = await api.getCommonField(field.name);
                          if (commonField && commonField.values && Array.isArray(commonField.values)) {
                            const existingValues = Array.isArray(arrayValue) ? arrayValue : [];
                            const newValues = [...existingValues];
                            commonField.values.forEach((val: any) => {
                              if (!newValues.includes(val)) {
                                newValues.push(val);
                              }
                            });
                            handleFieldChange(field.name, newValues);
                          }
                        } catch (error) {
                          console.error('Error fetching common values:', error);
                        }
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        },
                      }}
                    >
                      <AutoFixHighIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            {field.description && field.name !== 'targets' && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '1rem' }}>
                {isRestricted ? `${field.description} (Cannot be modified)` : field.description}
              </Typography>
            )}
            {numericArrayField && !isRestricted && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontSize: '0.95rem' }}>
                Press {shortcutToLabel(shortcut)} to insert a numeric placeholder like §1§.
              </Typography>
            )}
            {field.name === 'targets' && (
              <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                Enter domains, IPs, or URLs. Use &lt;random_string&gt; for dynamic values.
              </Alert>
            )}
            
            {isCompactArray ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                {displayItems.map((item: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flex: '0 0 auto',
                    }}
                  >
                    <TextField
                      size="small"
                      value={item}
                      onChange={(e) => !isRestricted && handleArrayItemChange(field.name, index, e.target.value, field)}
                      placeholder={`Item ${index + 1}`}
                      disabled={isRestricted}
                      inputProps={getArrayItemInputProps(field, index)}
                      sx={{
                        width: 180,
                        '& .MuiOutlinedInput-root': {
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '1.4rem',
                          '& input': {
                            py: 1.2,
                          },
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleArrayRemove(field.name, index)}
                      sx={{
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.12),
                        },
                      }}
                      disabled={isRestricted}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                
                {/* Add button inline */}
                {!isRestricted && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleArrayAdd(field.name)}
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.9rem',
                      minWidth: 120,
                    }}
                  >
                    Add
                  </Button>
                )}
              </Box>
            ) : (
              displayItems.map((item: any, index: number) => {
                const parsed = field.name === 'targets' ? parseTarget(item) : null;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => !isRestricted && handleArrayItemChange(field.name, index, e.target.value, field)}
                      placeholder={field.name === 'targets' ? 'example.com or 192.168.1.1' : `Item ${index + 1}`}
                      disabled={isRestricted}
                      error={field.name === 'targets' && parsed ? !parsed.isValid && item.trim() !== '' : false}
                      helperText={field.name === 'targets' && parsed && !parsed.isValid && item.trim() !== '' ? parsed.error : ''}
                      inputProps={getArrayItemInputProps(field, index)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '1.4rem',
                          '& input': {
                            py: 1.2,
                          },
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleArrayRemove(field.name, index)}
                      sx={{ 
                        ml: 1,
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.12),
                        },
                      }}
                      disabled={isRestricted}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
            
            {/* Show More Button for collapsed arrays */}
            {!isExpanded && hiddenCount > 0 && (
              <Button
                size="small"
                onClick={() => setExpandedArrays(prev => ({ ...prev, [field.name]: true }))}
                sx={{
                  mb: 1,
                  color: theme.palette.primary.main,
                  fontFamily: '"Space Mono", monospace',
                }}
              >
                Show {hiddenCount} more...
              </Button>
            )}
            
            {/* Show Less Button */}
            {isExpanded && arrayValue.length > 3 && (
              <Button
                size="small"
                onClick={() => setExpandedArrays(prev => ({ ...prev, [field.name]: false }))}
                sx={{
                  mb: 1,
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                }}
              >
                Show less
              </Button>
            )}
            
            {/* Add New Item Button - only for non-compact arrays (compact has inline Add) */}
            {!isCompactArray && !isRestricted && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleArrayAdd(field.name)}
                sx={{
                  mt: 1,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.95rem',
                  py: 1,
                }}
              >
                Add {field.name.replace(/_/g, ' ')}
              </Button>
            )}
          </Box>
        );

      case 'object':
        const objectValue = typeof value === 'object' && value !== null ? value : {};
        const fieldState = objectFieldStates[field.name] || { newKey: '', newValue: '' };
        const isButtonDisabled = !fieldState.newKey || !fieldState.newValue;

        return (
          <Box key={field.name} sx={{ mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 1.5,
                fontSize: '1.15rem',
                fontFamily: '"Space Mono", monospace',
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {field.name.replace(/_/g, ' ').toUpperCase()}
              {field.required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
              {isRestricted && <Chip label="Read-only" size="small" color="default" sx={{ ml: 1 }} />}
            </Typography>
            {field.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '1rem' }}>
                {isRestricted ? `${field.description} (Cannot be modified)` : field.description}
              </Typography>
            )}
            
            {/* Existing key-value pairs */}
            {Object.entries(objectValue).map(([key, val]) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TextField
                  size="medium"
                  value={key}
                  label="Key"
                  disabled
                  sx={{ 
                    width: '200px',
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Space Mono", monospace',
                    },
                  }}
                />
                <TextField
                  size="medium"
                  label="Value"
                  value={val as string}
                  onChange={(e) => !isRestricted && handleObjectAdd(field.name, key, e.target.value)}
                  inputProps={{ 'data-field-name': field.name, 'data-field-key': key } as any}
                  sx={{ 
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Space Mono", monospace',
                    },
                  }}
                  disabled={isRestricted}
                />
                <IconButton
                  size="medium"
                  onClick={() => handleObjectRemove(field.name, key)}
                  disabled={isRestricted}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.12),
                    },
                  }}
                >
                  <RemoveIcon />
                </IconButton>
              </Box>
            ))}

            {/* Add new key-value pair */}
            {!isRestricted && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <TextField
                  size="medium"
                  placeholder="Key"
                  label="New Key"
                  value={fieldState.newKey}
                  onChange={(e) => setObjectFieldStates(prev => ({
                    ...prev,
                    [field.name]: { ...fieldState, newKey: e.target.value }
                  }))}
                  sx={{ 
                    width: '200px',
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Space Mono", monospace',
                    },
                  }}
                />
                <TextField
                  size="medium"
                  placeholder="Value"
                  label="New Value"
                  value={fieldState.newValue}
                  onChange={(e) => setObjectFieldStates(prev => ({
                    ...prev,
                    [field.name]: { ...fieldState, newValue: e.target.value }
                  }))}
                  sx={{ 
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      fontFamily: '"Space Mono", monospace',
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    if (fieldState.newKey && fieldState.newValue) {
                      handleObjectAdd(field.name, fieldState.newKey, fieldState.newValue);
                      setObjectFieldStates(prev => ({
                        ...prev,
                        [field.name]: { newKey: '', newValue: '' }
                      }));
                    }
                  }}
                  disabled={isButtonDisabled}
                  sx={{ 
                    minWidth: 80,
                    fontFamily: '"Space Mono", monospace',
                  }}
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.name.replace(/_/g, ' ').toUpperCase()}
            value={value}
            onChange={(e) => !isRestricted && handleFieldChange(field.name, e.target.value)}
            helperText={isRestricted ? `${field.description} (Cannot be modified)` : field.description}
            required={field.required}
            disabled={isRestricted}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '1rem',
              },
              '& .MuiInputLabel-root': {
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.9rem',
              },
            }}
          />
        );
    }
  };

  // Group fields by requirement status, excluding targets and threads
  const hiddenFields = computeHiddenFields(attackType.id, configuration);
  const excludedFields = new Set(['targets', 'threads', ...Array.from(hiddenFields)]);
  const requiredFields = parsedSchema.fields.filter(f => f.required && !excludedFields.has(f.name));
  const optionalFields = parsedSchema.fields.filter(f => !f.required && !excludedFields.has(f.name));

  // Default (non-collapsed) should contain anything currently present in config; Advanced contains the rest.
  const isPresent = (value: any) => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  const defaultOptionalFields = optionalFields.filter(f => isPresent((configuration as any)?.[f.name]));
  const advancedOptionalFields = optionalFields.filter(f => !isPresent((configuration as any)?.[f.name]));

  const isCarpetBombing = attackType.id === 'attack_id_76';
  const isCombinedVolumetric = attackType.id === 'attack_id_70';
  const isHttpAttack = attackType.attack_class === 'application';
  const fieldsByName = React.useMemo(() => {
    const map = new Map<string, AttackConfigField>(parsedSchema.fields.map((field) => [field.name, field]));
    if (isCarpetBombing) {
      Object.values(CARPET_BOMBING_FALLBACK_FIELDS).forEach((field) => {
        if (!map.has(field.name)) map.set(field.name, field);
      });
    }
    return map;
  }, [isCarpetBombing, parsedSchema.fields]);

  const renderNamedField = (name: string) => {
    if (excludedFields.has(name)) return null;
    const field = fieldsByName.get(name);
    if (!field) return null;
    return (
      <Grid item key={name} {...getFieldGridProps(field)}>
        {renderField(field)}
      </Grid>
    );
  };

  const isEnabled = (key: string, defaultValue: boolean) => {
    const raw = (configuration as any)?.[key];
    if (raw === undefined) return defaultValue;
    return Boolean(raw);
  };

  // Carpet bombing: which volumetric card hosts the shared attack_interval field
  const enabledVolumetricToggles = [
    { key: 'include_udp_flood', defaultEnabled: true },
    { key: 'include_tcp_flood', defaultEnabled: true },
    { key: 'include_icmp_flood', defaultEnabled: true },
    { key: 'include_fragmented_udp_flood', defaultEnabled: false },
  ];

  const firstEnabledVolumetricToggleKey =
    enabledVolumetricToggles.find(({ key, defaultEnabled }) => isEnabled(key, defaultEnabled))?.key ?? null;

  // Combined Volumetric: which card hosts the shared attack_interval field
  const cvToggles = [
    { key: 'udp_flood', defaultEnabled: true },
    { key: 'tcp_flood', defaultEnabled: true },
    { key: 'icmp_flood', defaultEnabled: true },
    { key: 'fragmented_udp_flood', defaultEnabled: false },
  ];

  const firstEnabledCvToggleKey =
    cvToggles.find(({ key, defaultEnabled }) => isEnabled(key, defaultEnabled))?.key ?? null;

  const renderAttackCard = (options: {
    title: string;
    toggleKey: string;
    description?: string;
    defaultEnabled: boolean;
    fieldNames: string[];
    includeSharedInterval?: boolean;
  }) => {
    const { title, toggleKey, description, defaultEnabled, fieldNames, includeSharedInterval } = options;
    const enabled = isEnabled(toggleKey, defaultEnabled);
    const isRestricted = restrictedFields.includes(toggleKey);

    return (
      <Box
        sx={{
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.9)}`,
          background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.92),
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.12)}, transparent)`,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
            {description && (
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{description}</Typography>
            )}
          </Box>

          <Switch
            checked={enabled}
            onChange={(e) => !isRestricted && handleFieldChange(toggleKey, e.target.checked)}
            disabled={isRestricted}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: theme.palette.primary.main },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.main },
            }}
          />
        </Box>

        <Collapse in={enabled} timeout={200} unmountOnExit>
          <Box sx={{ p: 2, pt: 1.5 }}>
            <Grid container spacing={2}>
              {includeSharedInterval ? renderNamedField('attack_interval') : null}
              {fieldNames.map(renderNamedField)}
            </Grid>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderGroupHeader = (title: string) => (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"JetBrains Mono", monospace',
          fontWeight: 700,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      <Divider
        sx={{
          mt: 1,
          background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.35)}, transparent)`,
        }}
      />
    </Box>
  );

  // ─── Cache Busting helpers ────────────────────────────────────────────────
  // Types (CbPartType, CbPartConfig, CbPart, CbParam, CB_PART_LABELS, defaultCbPart)
  // are imported from '../types/payloadTypes'.

  const getCbParams = (): CbParam[] => {
    const raw = (configuration as any).cache_busting_params;
    return Array.isArray(raw) ? raw : [];
  };

  const updateCbParams = (updated: CbParam[]) => {
    handleFieldChange('cache_busting_params', updated);
  };

  const updateCbPart = (idx: number, side: 'key' | 'value', updated: CbPart) => {
    const params = getCbParams();
    const next = [...params];
    next[idx] = { ...next[idx], [side]: updated };
    updateCbParams(next);
  };

  const addCbParam = () => {
    updateCbParams([...getCbParams(), { key: defaultCbPart('static'), value: defaultCbPart('random_string') }]);
  };

  const removeCbParam = (idx: number) => {
    const params = getCbParams();
    updateCbParams(params.filter((_, i) => i !== idx));
  };

  const renderCbPartConfig = (idx: number, side: 'key' | 'value', part: CbPart) => {
    const update = (patch: Partial<CbPart>) => updateCbPart(idx, side, { ...part, ...patch });
    const updateConfig = (patch: Partial<CbPartConfig>) =>
      update({ config: { ...(part.config ?? {}), ...patch } });

    const cfg = part.config ?? {};
    const monoSx = { fontFamily: '"Space Mono", monospace', fontSize: '0.78rem' };
    const chipSx = (active: boolean) => ({
      fontFamily: '"Space Mono", monospace',
      fontSize: '0.7rem',
      cursor: 'pointer',
      borderColor: active ? theme.palette.primary.main : alpha(theme.palette.divider, 0.7),
      // In dark mode the primary is bright cyan — force dark text so it's readable.
      ...(active && theme.palette.mode === 'dark' && {
        '& .MuiChip-label': { color: '#0a0e17', fontWeight: 700 },
      }),
    });

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Type selector */}
        <FormControl size="small" fullWidth>
          <InputLabel sx={{ fontSize: '0.78rem' }}>{side === 'key' ? 'Key type' : 'Value type'}</InputLabel>
          <Select
            value={part.type}
            label={side === 'key' ? 'Key type' : 'Value type'}
            onChange={(e) => update(defaultCbPart(e.target.value as CbPartType))}
            sx={monoSx}
          >
            {(Object.keys(CB_PART_LABELS) as CbPartType[]).map((t) => (
              <MenuItem key={t} value={t} sx={monoSx}>{CB_PART_LABELS[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Static ── */}
        {part.type === 'static' && (
          <TextField
            size="small"
            fullWidth
            placeholder="literal value"
            value={part.value ?? ''}
            onChange={(e) => update({ value: e.target.value })}
            sx={{ '& input': monoSx }}
          />
        )}

        {/* ── Random String ── */}
        {part.type === 'random_string' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Length range */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="min length" type="number"
                value={cfg.min_l ?? 8}
                onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
              <TextField size="small" label="max length" type="number"
                value={cfg.max_l ?? 14}
                onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
            </Box>
            {/* Character class quick-chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {([
                ['a-z', 'lowercase', cfg.lowercase !== false],
                ['0-9', 'digits', cfg.digits !== false],
                ['A-Z', 'uppercase', !!cfg.uppercase],
                ['!@#', 'special_chars', !!cfg.special_chars],
              ] as [string, keyof CbPartConfig, boolean][]).map(([label, key, checked]) => (
                <Chip key={key} label={label} size="small" clickable
                  onClick={() => updateConfig({ [key]: !checked })}
                  color={checked ? 'primary' : 'default'}
                  variant={checked ? 'filled' : 'outlined'}
                  sx={chipSx(checked)}
                />
              ))}
            </Box>
            {/* Custom charset override */}
            <TextField size="small" label="Custom charset (overrides chips)"
              placeholder="e.g. abcABC123 or leave empty"
              value={cfg.custom_chars ?? ''}
              onChange={(e) => updateConfig({ custom_chars: e.target.value })}
              sx={{ '& input': monoSx }}
              helperText={cfg.custom_chars ? 'Using custom charset — chip settings ignored' : 'Optional: type exact characters to sample from'}
              FormHelperTextProps={{ sx: { fontSize: '0.68rem', mt: 0.25 } }}
            />
          </Box>
        )}

        {/* ── Random Number ── */}
        {part.type === 'random_number' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Mode toggle — segmented control */}
            <Box sx={{
              display: 'inline-flex',
              alignSelf: 'flex-start',
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              overflow: 'hidden',
            }}>
              {(['digit-length', 'value-range'] as const).map((mode, i) => {
                const active = mode === 'value-range' ? !!cfg.range_mode : !cfg.range_mode;
                return (
                  <Button
                    key={mode}
                    disableElevation
                    onClick={() => updateConfig({ range_mode: mode === 'value-range' })}
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      px: 1.75,
                      py: 0.65,
                      minWidth: 0,
                      textTransform: 'none',
                      borderRadius: 0,
                      borderRight: i === 0 ? `1px solid ${alpha(theme.palette.divider, 0.7)}` : 'none',
                      backgroundColor: active ? theme.palette.primary.main : 'transparent',
                      color: active
                        ? (theme.palette.mode === 'dark' ? '#0a0e17' : '#ffffff')
                        : theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: active
                          ? theme.palette.primary.dark ?? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    {mode === 'digit-length' ? 'Digit length' : 'Value range'}
                  </Button>
                );
              })}
            </Box>

            {!cfg.range_mode ? (
              /* Digit-length mode */
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="min digits" type="number"
                    value={cfg.min_l ?? 8}
                    onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1, '& input': monoSx }}
                  />
                  <TextField size="small" label="max digits" type="number"
                    value={cfg.max_l ?? 14}
                    onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                    inputProps={{ min: 1 }}
                    sx={{ flex: 1, '& input': monoSx }}
                  />
                </Box>
                <Chip label="leading zeros" size="small" clickable
                  onClick={() => updateConfig({ allow_leading_zeros: !cfg.allow_leading_zeros })}
                  color={cfg.allow_leading_zeros ? 'primary' : 'default'}
                  variant={cfg.allow_leading_zeros ? 'filled' : 'outlined'}
                  sx={{ ...chipSx(!!cfg.allow_leading_zeros), alignSelf: 'flex-start' }}
                />
              </>
            ) : (
              /* Value-range mode */
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" label="min value" type="number"
                  value={cfg.min_value ?? 0}
                  onChange={(e) => updateConfig({ min_value: parseInt(e.target.value) || 0 })}
                  sx={{ flex: 1, '& input': monoSx }}
                />
                <TextField size="small" label="max value" type="number"
                  value={cfg.max_value ?? 99999}
                  onChange={(e) => updateConfig({ max_value: parseInt(e.target.value) || 0 })}
                  sx={{ flex: 1, '& input': monoSx }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* ── Random Base64 ── */}
        {part.type === 'random_base64' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" label="min length" type="number"
                value={cfg.min_l ?? 8}
                onChange={(e) => updateConfig({ min_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
              <TextField size="small" label="max length" type="number"
                value={cfg.max_l ?? 14}
                onChange={(e) => updateConfig({ max_l: Math.max(1, parseInt(e.target.value) || 1) })}
                inputProps={{ min: 1 }}
                sx={{ flex: 1, '& input': monoSx }}
              />
            </Box>
            <Chip label="URL-safe  (-_ instead of +/)" size="small" clickable
              onClick={() => updateConfig({ url_safe: !cfg.url_safe })}
              color={cfg.url_safe ? 'primary' : 'default'}
              variant={cfg.url_safe ? 'filled' : 'outlined'}
              sx={{ ...chipSx(!!cfg.url_safe), alignSelf: 'flex-start' }}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderCacheBustingCard = () => {
    const enabled = Boolean((configuration as any).cache_busting_enabled);
    const params = getCbParams();
    const isRestricted = restrictedFields.includes('cache_busting_enabled');

    return (
      <Box
        sx={{
          mt: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.9)}`,
          background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.55 : 0.92),
          backdropFilter: 'blur(10px)',
          overflow: 'hidden',
        }}
      >
        {/* Header / toggle row */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.12)}, transparent)`,
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 700, color: 'text.primary' }}>
              Cache Busting
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
              Append dynamic URL parameters to bypass caches on every request.
            </Typography>
          </Box>
          <Switch
            checked={enabled}
            onChange={(e) => !isRestricted && handleFieldChange('cache_busting_enabled', e.target.checked)}
            disabled={isRestricted}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: theme.palette.primary.main },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.palette.primary.main },
            }}
          />
        </Box>

        <Collapse in={enabled} timeout={200} unmountOnExit>
          <Box sx={{ p: 2, pt: 1.5 }}>
            {/* Info note */}
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2, fontFamily: '"Space Mono", monospace' }}>
              All values are URL-encoded automatically. Special characters in keys and values are safe to use.
            </Typography>

            {params.length === 0 && (
              <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2, fontStyle: 'italic' }}>
                No parameters yet. Click "+ Add Parameter" to add one.
              </Typography>
            )}

            {params.map((param, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.5 : 0.8)}`,
                  overflow: 'hidden',
                  background: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.4 : 0.5),
                }}
              >
                {/* Row index + remove button */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1.5,
                  py: 0.5,
                  background: alpha(theme.palette.action.hover, 0.04),
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }}>
                  <Typography sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.65rem', color: 'text.disabled', letterSpacing: '0.08em' }}>
                    PARAM {idx + 1}
                  </Typography>
                  <Tooltip title="Remove parameter">
                    <IconButton size="small" onClick={() => removeCbParam(idx)}
                      sx={{ color: theme.palette.error.main, p: 0.4, '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.12) } }}
                    >
                      <RemoveIcon sx={{ fontSize: '0.95rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* KEY | VALUE two-pane layout */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  {/* KEY pane */}
                  <Box sx={{
                    p: 1.5,
                    borderRight: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                    borderTop: `2px solid ${theme.palette.primary.main}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.62rem',
                      fontFamily: '"Space Mono", monospace',
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      mb: 1,
                    }}>
                      Key
                    </Typography>
                    {renderCbPartConfig(idx, 'key', param.key ?? defaultCbPart('static'))}
                  </Box>

                  {/* VALUE pane */}
                  <Box sx={{
                    p: 1.5,
                    borderTop: `2px solid ${theme.palette.secondary.main}`,
                  }}>
                    <Typography sx={{
                      fontSize: '0.62rem',
                      fontFamily: '"Space Mono", monospace',
                      fontWeight: 700,
                      color: theme.palette.secondary.main,
                      textTransform: 'uppercase',
                      letterSpacing: '0.12em',
                      mb: 1,
                    }}>
                      Value
                    </Typography>
                    {renderCbPartConfig(idx, 'value', param.value ?? defaultCbPart('random_string'))}
                  </Box>
                </Box>
              </Box>
            ))}

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCbParam}
              variant="outlined"
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                borderColor: alpha(theme.palette.primary.main, 0.5),
                color: theme.palette.primary.main,
                '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              Add Parameter
            </Button>
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box ref={containerRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h5"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          Attack Configuration
        </Typography>
        
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.85rem',
            },
          }}
        >
          <ToggleButton value="form">
            <ViewListIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            Form
          </ToggleButton>
          <ToggleButton value="json">
            <CodeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Shortcut hint */}
      {viewMode === 'form' && (
        <Typography
          sx={{
            fontSize: '0.72rem',
            color: 'text.disabled',
            fontFamily: '"Space Mono", monospace',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          Press
          {recordingShortcut ? (
            <Box
              tabIndex={0}
              onKeyDown={handleShortcutRecordKeyDown}
              onBlur={() => setRecordingShortcut(false)}
              autoFocus
              sx={{
                px: 0.85,
                py: 0.2,
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}`,
                background: alpha(theme.palette.primary.main, 0.08),
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.72rem',
                color: theme.palette.primary.main,
                cursor: 'text',
                outline: 'none',
                lineHeight: 1.4,
                mx: 0.4,
              }}
              component="span"
            >
              Press new shortcut...
            </Box>
          ) : (
            <Tooltip title="Click to change shortcut">
              <Chip
                label={shortcutToLabel(shortcut)}
                size="small"
                variant="outlined"
                onClick={() => setRecordingShortcut(true)}
                icon={<RecordIcon sx={{ fontSize: '0.6rem !important' }} />}
                sx={{
                  mx: 0.4,
                  height: 20,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: '0.72rem',
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              />
            </Tooltip>
          )}
          in any text field to insert a §N§ payload function placeholder.
          <Box component="span" sx={{ color: 'text.disabled' }}>
            {' '} (click shortcut chip to change)
          </Box>
        </Typography>
      )}

      {viewMode === 'form' ? (
        <>
          {isCombinedVolumetric ? (
            <>
              {fieldsByName.has('attack_duration') && !excludedFields.has('attack_duration') && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    {renderNamedField('attack_duration')}
                  </Grid>
                </Box>
              )}

              {renderGroupHeader('Volumetric Attacks')}
              {renderAttackCard({
                title: 'UDP Flood',
                toggleKey: 'udp_flood',
                description: 'Packet-based UDP flood against target ports.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledCvToggleKey === 'udp_flood',
                fieldNames: ['udp_ports', 'udp_data_size'],
              })}
              {renderAttackCard({
                title: 'TCP SYN Flood',
                toggleKey: 'tcp_flood',
                description: 'TCP flood using SYN packets.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledCvToggleKey === 'tcp_flood',
                fieldNames: ['tcp_ports'],
              })}
              {renderAttackCard({
                title: 'ICMP Flood',
                toggleKey: 'icmp_flood',
                description: 'ICMP echo flood.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledCvToggleKey === 'icmp_flood',
                fieldNames: [],
              })}
              {renderAttackCard({
                title: 'Fragmented UDP Flood',
                toggleKey: 'fragmented_udp_flood',
                description: 'UDP flood with fragmentation parameters.',
                defaultEnabled: false,
                includeSharedInterval: firstEnabledCvToggleKey === 'fragmented_udp_flood',
                fieldNames: ['fragmented_udp_ports', 'fragmented_udp_data_size'],
              })}
            </>
          ) : isCarpetBombing ? (
            <>
              {renderGroupHeader('General Settings')}
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  {[
                    'mode',
                    'interval_seconds',
                    'random_interval',
                    'interval_seconds_min',
                    'interval_seconds_max',
                  ].map(renderNamedField)}
                </Grid>
              </Box>

              {renderGroupHeader('Volumetric Attacks')}
              {renderAttackCard({
                title: 'UDP Flood',
                toggleKey: 'include_udp_flood',
                description: 'Packet-based UDP flood against target ports.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledVolumetricToggleKey === 'include_udp_flood',
                fieldNames: ['udp_ports', 'udp_data_size'],
              })}
              {renderAttackCard({
                title: 'TCP SYN Flood',
                toggleKey: 'include_tcp_flood',
                description: 'TCP flood using SYN packets.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledVolumetricToggleKey === 'include_tcp_flood',
                fieldNames: ['tcp_ports', 'tcp_flags'],
              })}
              {renderAttackCard({
                title: 'ICMP Flood',
                toggleKey: 'include_icmp_flood',
                description: 'ICMP echo flood.',
                defaultEnabled: true,
                includeSharedInterval: firstEnabledVolumetricToggleKey === 'include_icmp_flood',
                fieldNames: ['icmp_type', 'icmp_packet_size'],
              })}
              {renderAttackCard({
                title: 'Fragmented UDP Flood',
                toggleKey: 'include_fragmented_udp_flood',
                description: 'UDP flood with fragmentation parameters.',
                defaultEnabled: false,
                includeSharedInterval: firstEnabledVolumetricToggleKey === 'include_fragmented_udp_flood',
                fieldNames: ['fragmented_udp_ports', 'fragmented_udp_data_size', 'fragment_size'],
              })}

              {renderGroupHeader('Infrastructure Attacks')}
              {renderAttackCard({
                title: 'SSL/TLS Flood',
                toggleKey: 'include_ssl_flood',
                description: 'Repeated TLS handshakes against the target.',
                defaultEnabled: false,
                fieldNames: ['ssl_ports', 'ssl_timeout'],
              })}
              {renderAttackCard({
                title: 'TCP Connection Flood',
                toggleKey: 'include_tcp_connection_flood',
                description: 'Rapid TCP connect/hold/close cycles.',
                defaultEnabled: false,
                fieldNames: ['tcp_conn_ports', 'tcp_conn_connection_time'],
              })}

              {renderGroupHeader('Application Attacks')}
              {renderAttackCard({
                title: 'GET Flood',
                toggleKey: 'include_get_flood',
                description: 'HTTP GET request flood.',
                defaultEnabled: false,
                fieldNames: ['get_flood_timeout'],
              })}
              {renderAttackCard({
                title: 'Large Data POST Flood',
                toggleKey: 'include_post_flood',
                description: 'HTTP POST with large payloads.',
                defaultEnabled: false,
                fieldNames: ['post_data_size', 'post_content_type'],
              })}
              {renderAttackCard({
                title: 'Slow POST',
                toggleKey: 'include_slow_post',
                description: 'Slowly send a large POST body to hold server resources.',
                defaultEnabled: false,
                fieldNames: ['slowpost_content_length', 'slowpost_interval', 'slowpost_send_rate'],
              })}
              {renderAttackCard({
                title: 'Slow GET (Slowloris)',
                toggleKey: 'include_slow_get',
                description: 'Hold many connections open by sending incomplete headers.',
                defaultEnabled: false,
                fieldNames: ['slowloris_ports', 'slowloris_sleep_time', 'slowloris_packet_size'],
              })}
            </>
          ) : (
            <>
              {requiredFields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    {requiredFields.map(field => (
                      <Grid item key={field.name} {...getFieldGridProps(field)}>
                        {renderField(field)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {defaultOptionalFields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    {defaultOptionalFields.map(field => (
                      <Grid item key={field.name} {...getFieldGridProps(field)}>
                        {renderField(field)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {advancedOptionalFields.length > 0 && (
                <Box sx={{ mt: (requiredFields.length > 0 || defaultOptionalFields.length > 0) ? 3 : 0 }}>
                  <Divider
                    sx={{
                      mb: 2,
                      background: `linear-gradient(90deg, transparent, ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}, transparent)`,
                    }}
                  />
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.9)}`,
                      background: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.6 : 0.9),
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <ButtonBase
                      onClick={() => setShowAdvanced(v => !v)}
                      sx={{
                        width: '100%',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderRadius: 2,
                        textAlign: 'left',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.05 : 0.08),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1rem',
                          }}
                        >
                          Advanced Parameters
                        </Typography>
                        <Chip
                          label={`${advancedOptionalFields.length}`}
                          size="small"
                          sx={{
                            backgroundColor: 'action.selected',
                            color: 'text.secondary',
                            fontFamily: '"Space Mono", monospace',
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                      <ExpandMoreIcon
                        sx={{
                        color: theme.palette.primary.main,
                          transition: 'transform 0.2s ease',
                          transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    </ButtonBase>

                    <Collapse in={showAdvanced} timeout={200} unmountOnExit>
                      <Box sx={{ p: 2, pt: 0 }}>
                        <Grid container spacing={2}>
                          {advancedOptionalFields.map(field => (
                            <Grid item key={field.name} {...getFieldGridProps(field)}>
                              {renderField(field)}
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              )}

              {isHttpAttack && renderCacheBustingCard()}
            </>
          )}

          {/* Payload Functions card - shown for all attack types when §N§ placeholders are found */}
          {(() => {
            const ids = scanPayloadPlaceholders(configuration);
            if (ids.length === 0) return null;
            return (
              <PayloadFunctionsCard
                placeholderIds={ids}
                payloadFunctions={getPayloadFunctions()}
                onChange={updatePayloadFunctions}
              />
            );
          })()}

          {/* Configuration Preview in Form Mode */}
          {showConfigPreview && (
            <Box sx={{ mt: 4 }}>
              <Divider
                sx={{
                  mb: 2,
                  background: `linear-gradient(90deg, transparent, ${alpha(
                    theme.palette.primary.main,
                    0.3
                  )}, transparent)`,
                }}
              />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Configuration Preview
              </Typography>
              <Box
                component="pre"
                sx={{
                  background: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.6 : 0.95),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  p: 3,
                  borderRadius: 2,
                  fontSize: '0.85rem',
                  overflow: 'auto',
                  maxHeight: 300,
                  fontFamily: '"Space Mono", monospace',
                  color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.text.primary,
                  lineHeight: 1.6,
                }}
              >
                {JSON.stringify(configuration, null, 2)}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            JSON Configuration Editor
          </Typography>
          
          {jsonError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>JSON Error:</strong> {jsonError}
            </Alert>
          )}
          
          <TextField
            multiline
            fullWidth
            rows={20}
            value={jsonString}
            onChange={(e) => handleJsonChange(e.target.value)}
            placeholder="Enter valid JSON configuration..."
            error={!!jsonError}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }
            }}
            helperText={jsonError ? 'Please fix the JSON syntax error above' : 'Edit the JSON configuration directly. Changes will be applied in real-time if the JSON is valid.'}
          />
        </Box>
      )}
    </Box>
  );
};

export default AttackConfigForm; 